import { action, get } from "@ember/object"
import type ObjectProxy from "@ember/object/proxy"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import type {
  Group,
  GroupMembershipAccepted,
  GroupMembershipInvited,
  GroupMembershipPotential,
  GroupMembershipRequested,
  IndividualMembership,
} from "@shared/models/group"
import { ALL_SCHOOL_YEAR_IDS } from "@shared/models/school-year"
import {
  ACTIONS_ALL_COLUMN,
  ACTIONS_CHANGE_ROLE,
  NAME_COLUMN,
  SHARED_PLANBOOKS_COLUMN,
  TEAM_COLUMN,
  UPGRADED_ADMIN_COLUMN,
  UPGRADED_MEMBER_COLUMN,
} from "cc-frontend/components/group/members-table"
import findDocument from "cc-frontend/helpers/find-document"
import FindManyAltogether from "cc-frontend/helpers/find-many-altogether"
import type EmberStore from "cc-frontend/services/ember-store"
import type ModalManager from "cc-frontend/services/modal-manager"
import {
  getGroupId,
  getUserId,
  isGroupMembershipAccepted,
  isGroupMembershipGroup,
  isGroupMembershipInvited,
  isGroupMembershipPotential,
  isGroupMembershipRequested,
  membersWithAdminRole,
  membersWithMemberRole,
} from "cc-frontend/utils/group"
import { use } from "ember-could-get-used-to-this"
import { concat, find } from "lodash-es"
import * as R from "remeda" // tree-shaking supported!

interface Signature {
  Args: {
    group: Group
  }
}

export default class SchoolEditMembers extends Component<Signature> {
  @service("ember-store") declare store: EmberStore

  @service declare modalManager: ModalManager

  hasInserted = true
  schoolYearIds = ALL_SCHOOL_YEAR_IDS

  adminTableColumns = [
    NAME_COLUMN,
    UPGRADED_ADMIN_COLUMN,
    TEAM_COLUMN,
    SHARED_PLANBOOKS_COLUMN,
    ACTIONS_CHANGE_ROLE,
    ACTIONS_ALL_COLUMN,
  ]

  teacherTableColumns = [
    NAME_COLUMN,
    UPGRADED_MEMBER_COLUMN,
    TEAM_COLUMN,
    SHARED_PLANBOOKS_COLUMN,
    ACTIONS_CHANGE_ROLE,
    ACTIONS_ALL_COLUMN,
  ]

  get parentGroupId(): string | null {
    if (this.args.group.attributes.usedAs === "school") {
      const districtAncestor = this.args.group.attributes.cachedAncestry.find(
        (ancestry) => ancestry.attributes.usedAs === "school-network"
      )
      if (districtAncestor) return districtAncestor.attributes.groupId
    }
    return null
  }

  @findDocument("parentGroupId", "group") declare parentGroup: ObjectProxy<Group>

  get teamIds() {
    return R.pipe(
      get(this.args.group.attributes, "memberships"),
      R.filter(isGroupMembershipGroup),
      R.map(getGroupId)
    )
  }

  get allUserIds() {
    return R.pipe(
      get(this.args.group.attributes, "memberships"),
      R.filter(
        (membership) =>
          isGroupMembershipAccepted(membership) ||
          isGroupMembershipRequested(membership) ||
          isGroupMembershipPotential(membership)
      ),
      R.map(getUserId)
    )
  }

  @use userSummaries = new FindManyAltogether(() => ["user-summary", this.allUserIds])

  get teacherMemberships() {
    if (this.userSummaries.isFulfilled !== true) return []
    let accepted = R.pipe(
      get(this.args.group.attributes, "memberships"),
      R.filter(membersWithMemberRole),
      R.filter(isGroupMembershipAccepted),
      R.map(this.addSortNameByUser),
      R.compact,
      R.sortBy((membership) => membership.meta?.sortName)
    )
    let invited = R.pipe(
      get(this.args.group.attributes, "memberships"),
      R.filter(membersWithMemberRole),
      R.filter(isGroupMembershipInvited),
      R.map(this.addSortNameByEmail),
      R.sortBy((membership) => membership.meta.sortName)
    )
    let requested = R.pipe(
      get(this.args.group.attributes, "memberships"),
      R.filter(membersWithMemberRole),
      R.filter(isGroupMembershipRequested),
      R.map(this.addSortNameByUser),
      R.compact,
      R.sortBy((membership) => membership.meta?.sortName)
    )
    let potential = R.pipe(
      get(this.args.group.attributes, "memberships"),
      R.filter(membersWithMemberRole),
      R.filter(isGroupMembershipPotential),
      R.map(this.addSortNameByUser),
      R.compact,
      R.sortBy((membership) => membership.meta?.sortName)
    )

    return concat<IndividualMembership | GroupMembershipRequested | GroupMembershipPotential>(
      accepted,
      invited,
      requested,
      potential
    )
  }

  get adminMemberships() {
    if (this.userSummaries.isFulfilled !== true) return []
    let accepted = R.pipe(
      get(this.args.group.attributes, "memberships"),
      R.filter(membersWithAdminRole),
      R.filter(isGroupMembershipAccepted),
      R.map(this.addSortNameByUser),
      R.compact,
      R.sortBy((membership) => membership.meta?.sortName)
    )

    let invited = R.pipe(
      get(this.args.group.attributes, "memberships"),
      R.filter(membersWithAdminRole),
      R.filter(isGroupMembershipInvited),
      R.map(this.addSortNameByEmail),
      R.sortBy((membership) => membership.meta.sortName)
    )

    let requested = R.pipe(
      get(this.args.group.attributes, "memberships"),
      R.filter(membersWithAdminRole),
      R.filter(isGroupMembershipRequested),
      R.map(this.addSortNameByUser),
      R.compact,
      R.sortBy((membership) => membership.meta?.sortName)
    )

    let potential = R.pipe(
      get(this.args.group.attributes, "memberships"),
      R.filter(membersWithAdminRole),
      R.filter(isGroupMembershipPotential),
      R.map(this.addSortNameByUser),
      R.compact,
      R.sortBy((membership) => membership.meta?.sortName)
    )

    return concat<IndividualMembership | GroupMembershipRequested | GroupMembershipPotential>(
      accepted,
      invited,
      requested,
      potential
    )
  }

  @action addSortNameByUser<
    M extends GroupMembershipAccepted | GroupMembershipRequested | GroupMembershipPotential
  >(membership: M): M | null {
    if (this.userSummaries.content === null) return null
    let user = find(
      this.userSummaries.content,
      (summary) => summary.id === membership.attributes.userId
    )
    if (user === undefined) return null
    let sortName = get(user.attributes, "lastName") ?? ""
    membership.meta = { sortName: sortName }
    return membership
  }

  @action addSortNameByEmail(membership: GroupMembershipInvited): GroupMembershipInvited {
    let sortName = get(membership.attributes, "email") ?? ""
    membership.meta = { sortName: sortName }
    return membership
  }

  @action
  addTeacher(group) {
    this.modalManager.openModal("group/add-members", {
      group: group,
      showOnlyAdmins: false,
      makeAdmin: false,
    })
  }

  @action
  addAdmin(group) {
    this.modalManager.openModal("group/add-members", {
      group: group,
      showOnlyAdmins: true,
      parentGroup: null,
      makeAdmin: true,
    })
  }
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    SchoolEditMembers: typeof SchoolEditMembers
  }
}
